import React from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { 
  HiOutlineChartBar, 
  HiOutlineCpuChip, 
  HiOutlineCheckCircle 
} from 'react-icons/hi2';

const ResultsSectionDemo = () => {
  const [activeFeature, setActiveFeature] = useState('performance');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Feature data with corresponding images and content
  const features = [
    {
      id: 'performance',
      title: 'Signal Performance',
      description: 'Track the accuracy and performance of our algorithmic signals over time.',
      icon: <HiOutlineChartBar className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      image: '/assets/landingpage/chart_image.png',
      dots: ['bg-green-400', 'bg-cyan-400']
    },
    {
      id: 'accuracy',
      title: '95% Signal Accuracy',
      description: 'Our dual-model approach ensures high-precision signals with minimal false positives, giving you confidence in your trading decisions.',
      icon: <HiOutlineCheckCircle className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      image: '/assets/landingpage/chart_image.png',
      dots: ['bg-green-400', 'bg-green-500']
    },
    {
      id: 'realtime',
      title: 'Real-time Analysis',
      description: 'Get instant insights on thousands of stocks with our lightning-fast algorithmic scanning that processes market data in milliseconds.',
      icon: <HiOutlineChartBar className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
      image: '/assets/landingpage/chart_image.png',
      dots: ['bg-cyan-400', 'bg-blue-400']
    },
    {
      id: 'unbiased',
      title: 'Unbiased Insights',
      description: 'Remove emotional bias from your trading with our purely data-driven approach that focuses on market signals and technical analysis.',
      icon: <HiOutlineCpuChip className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      image: '/assets/landingpage/chart_image.png',
      dots: ['bg-blue-400', 'bg-purple-400']
    }
  ];

  const currentFeature = features.find(f => f.id === activeFeature);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const chartVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Enhanced Results Section Demo
          </h1>
          <p className="text-xl text-gray-600">
            Interactive 3D Coverflow Testimonial Carousel with Framer Motion Animations
          </p>
        </div>
        
        <motion.section 
          ref={ref}
          className="bg-gray-900 text-white py-20 lg:py-32 rounded-xl"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            {/* Headline */}
            <motion.div 
              className="text-center mb-16"
              variants={itemVariants}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
                Unbiased Insights,{' '}
                <span className="bg-gradient-to-r from-green-600 to-cyan-600 bg-clip-text text-transparent">Tangible Results</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto font-sans">
                Our algorithmic analysis delivers clear, data-driven signals that help you make informed trading decisions.
              </p>
            </motion.div>

            {/* Chart and Features Grid */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Interactive Chart/Image Container */}
              <motion.div 
                className="relative"
                variants={chartVariants}
              >
                <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold font-heading">{currentFeature.title}</h3>
                    <div className="flex space-x-2">
                      {currentFeature.dots.map((dotClass, index) => (
                        <div key={index} className={`w-3 h-3 ${dotClass} rounded-full`}></div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Animated Image Container */}
                  <div className="relative bg-gray-900 rounded overflow-hidden" style={{ height: 'calc(100% - 100px)' }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeFeature}
                        className="w-full h-full flex items-center justify-center"
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                      >
                        <img 
                          src={currentFeature.image} 
                          alt={`${currentFeature.title} Chart`} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback content when image fails to load */}
                        <div 
                          className="hidden w-full h-full items-center justify-center text-center p-8"
                          style={{ display: 'none' }}
                        >
                          <div>
                            <div className={`w-16 h-16 ${currentFeature.iconBg} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                              {currentFeature.icon}
                            </div>
                            <h4 className="text-xl font-semibold mb-2">{currentFeature.title}</h4>
                            <p className="text-gray-400 text-sm">{currentFeature.description}</p>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>

              {/* Interactive Feature Callouts */}
              <motion.div 
                className="space-y-8"
                variants={containerVariants}
              >
                {features.slice(1).map((feature, index) => (
                  <motion.div
                    key={feature.id}
                    variants={itemVariants}
                    className={`transition-all duration-300 cursor-pointer group ${
                      activeFeature === feature.id 
                        ? 'transform scale-105' 
                        : 'hover:transform hover:scale-102'
                    }`}
                  >
                    <button
                      onClick={() => setActiveFeature(feature.id)}
                      className="w-full text-left"
                    >
                      <div className={`flex items-start space-x-4 p-4 rounded-lg transition-all duration-300 ${
                        activeFeature === feature.id 
                          ? 'bg-gray-800 border border-gray-600' 
                          : 'hover:bg-gray-800/50'
                      }`}>
                        <div className={`flex-shrink-0 w-12 h-12 ${feature.iconBg} rounded-lg flex items-center justify-center transition-all duration-300 ${
                          activeFeature === feature.id 
                            ? 'scale-110 shadow-lg' 
                            : 'group-hover:scale-105'
                        }`}>
                          {feature.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-xl font-semibold mb-2 font-heading transition-colors duration-300 ${
                            activeFeature === feature.id 
                              ? 'text-white' 
                              : 'text-gray-300 group-hover:text-white'
                          }`}>
                            {feature.title}
                          </h3>
                          <p className={`font-sans transition-colors duration-300 ${
                            activeFeature === feature.id 
                              ? 'text-gray-200' 
                              : 'text-gray-400 group-hover:text-gray-300'
                          }`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.section>
        
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Features
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2">On-Scroll Animations</h3>
              <p className="text-gray-600">
                Beautiful staggered animations that trigger when the section comes into view
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2">Interactive Features</h3>
              <p className="text-gray-600">
                Click on different features to see the chart content change dynamically
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2">Smooth Transitions</h3>
              <p className="text-gray-600">
                Framer Motion powered animations with smooth image transitions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsSectionDemo;
